/**
 * 상담 신청 유입 정보 — 방문 시점 정보를 브라우저에 보관했다가 상담 신청(store-reserve)에 싣는다.
 * 서버가 store_reserve_attribution 에 저장하고 유입 경로(source)를 분류한다.
 *
 * - first: 처음 들어온 방문. 90일이 지나면 그다음 방문을 다시 first 로 잡는다.
 * - last: 신청 직전의 외부·캠페인 유입. 직접 유입(주소 입력·즐겨찾기)·새로고침·사이트 안 이동은
 *   덮지 않는다. 직접 유입만 있었다면 first 와 같다.
 * - submitUrl: 신청한 페이지.
 *
 * 브라우저 저장소가 막혀 있으면(개인정보 보호 모드 등) 이번 방문만 메모리에 들고 있다가 싣는다.
 * 분석용 부가 정보라 어떤 경우에도 예외를 밖으로 던지지 않는다.
 */

const STORAGE_KEY = 'dz:attribution';
const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const URL_MAX_LENGTH = 1000;
const PARAM_MAX_LENGTH = 255;

/** 지점 서브도메인(gangnam.drivingzone.co.kr 등)에서 온 이동은 사이트 안 이동으로 본다 */
const SITE_ROOT_DOMAIN = 'drivingzone.co.kr';

/** 주소에서 옮기는 캠페인 파라미터 (n_* = 네이버 검색광고 자동 추적, gclid = 구글 광고) */
const CAMPAIGN_PARAMS = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  nMedia: 'n_media',
  nKeyword: 'n_keyword',
  nQuery: 'n_query',
  gclid: 'gclid',
} as const;

type CampaignField = keyof typeof CAMPAIGN_PARAMS;

export type AttributionTouch = {
  landingUrl: string;
  referrer?: string;
  visitedAt: string;
} & Partial<Record<CampaignField, string>>;

export interface ReserveAttributionPayload {
  first?: AttributionTouch;
  last?: AttributionTouch;
  submitUrl?: string;
}

interface StoredAttribution {
  first: AttributionTouch;
  last: AttributionTouch;
}

let memoryAttribution: StoredAttribution | null = null;

function readStored(): StoredAttribution | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredAttribution;
      if (parsed?.first?.landingUrl && parsed?.last?.landingUrl) return parsed;
    }
  } catch {
    // 저장소를 못 쓰면 메모리 값으로 대신한다
  }
  return memoryAttribution;
}

function writeStored(value: StoredAttribution): void {
  memoryAttribution = value;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // 저장소를 못 쓰면 이번 방문 동안만 메모리에 둔다
  }
}

function isInternalReferrer(referrer: string): boolean {
  try {
    const host = new URL(referrer).hostname;
    return (
      host === window.location.hostname ||
      host === SITE_ROOT_DOMAIN ||
      host.endsWith(`.${SITE_ROOT_DOMAIN}`)
    );
  } catch {
    return false;
  }
}

/** 새로고침·뒤로가기는 새 유입이 아니다(document.referrer 가 처음 유입 값으로 남아 있다) */
function isRepeatedNavigation(): boolean {
  try {
    const [entry] = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[];
    return entry?.type === 'reload' || entry?.type === 'back_forward';
  } catch {
    return false;
  }
}

function buildCurrentTouch(): {
  touch: AttributionTouch;
  hasCampaign: boolean;
} {
  const url = new URL(window.location.href);
  const touch: AttributionTouch = {
    landingUrl: url.href.slice(0, URL_MAX_LENGTH),
    visitedAt: new Date().toISOString(),
  };

  const referrer = document.referrer;
  if (referrer && !isInternalReferrer(referrer)) {
    touch.referrer = referrer.slice(0, URL_MAX_LENGTH);
  }

  let hasCampaign = false;
  (Object.keys(CAMPAIGN_PARAMS) as CampaignField[]).forEach((field) => {
    const value = url.searchParams.get(CAMPAIGN_PARAMS[field])?.trim();
    if (value) {
      touch[field] = value.slice(0, PARAM_MAX_LENGTH);
      hasCampaign = true;
    }
  });

  return { touch, hasCampaign };
}

/** 문서를 새로 불러올 때 한 번 호출한다(AttributionTracker). 사이트 안 SPA 이동에서는 부르지 않는다. */
export function captureAttribution(): void {
  try {
    const { touch, hasCampaign } = buildCurrentTouch();
    const stored = readStored();
    const firstVisitedAt = stored ? Date.parse(stored.first.visitedAt) : NaN;

    if (
      !stored ||
      Number.isNaN(firstVisitedAt) ||
      Date.now() - firstVisitedAt > FIRST_TOUCH_TTL_MS
    ) {
      writeStored({ first: touch, last: touch });
      return;
    }

    // 캠페인 파라미터가 없으면 외부 사이트에서 새로 들어온 경우만 last 로 본다
    if (!hasCampaign && (!touch.referrer || isRepeatedNavigation())) return;
    writeStored({ first: stored.first, last: touch });
  } catch {
    // 분석용 부가 정보 — 실패해도 화면에 영향이 없어야 한다
  }
}

/** 상담 신청 요청에 싣는 유입 정보 */
export function getAttributionPayload(): ReserveAttributionPayload | undefined {
  try {
    const stored = readStored();
    return {
      first: stored?.first,
      last: stored?.last,
      submitUrl: window.location.href.slice(0, URL_MAX_LENGTH),
    };
  } catch {
    return undefined;
  }
}
